import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Loader2, Download, ArrowUpRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import PanoramaViewer from "@/components/PanoramaViewer";
import CameraCapture from "@/components/CameraCapture";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = ["Residential", "Commercial"];
const ROOMS_BY_CATEGORY = {
  Residential: ["Living Room", "Modular Kitchen", "Master Bedroom", "Walk-in Wardrobe", "Dining Room", "Home Office"],
  Commercial: ["Office", "Restaurant", "Café / Bar", "Retail Shop", "Salon / Clinic", "Boutique / Showroom"],
};
const STYLES = ["Modern Minimal", "Warm Contemporary", "Luxe Classic", "Scandinavian", "Indian Contemporary", "Industrial"];

const Chip = ({ active, children, ...p }) => (
  <button
    {...p}
    className={`px-4 py-2 text-sm border transition-colors duration-300 ${
      active ? "bg-[#143D63] text-[#F9F8F6] border-[#143D63]" : "bg-white text-[#143D63] border-[#143D63]/25 hover:border-[#143D63]"
    }`}
  >
    {children}
  </button>
);

export default function AIStudio() {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [roomType, setRoomType] = useState(ROOMS_BY_CATEGORY[CATEGORIES[0]][0]);
  const [style, setStyle] = useState(STYLES[0]);
  const [colorTheme, setColorTheme] = useState("");
  const [notes, setNotes] = useState("");
  const [sourceImage, setSourceImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState("2d");
  const navigate = useNavigate();

  const switchCategory = (c) => {
    setCategory(c);
    setRoomType(ROOMS_BY_CATEGORY[c][0]);
  };

  const attachAndBook = () => {
    if (result?.image) {
      localStorage.setItem(
        "zebo_ai_design",
        JSON.stringify({
          image: result.image,
          panorama: result.panorama || null,
          prompt: `${style} ${roomType}${colorTheme ? " · " + colorTheme : ""}`,
        })
      );
    }
    navigate("/consultation");
  };

  const generate = async () => {
    setLoading(true);
    setResult(null);
    setView("2d");
    try {
      const { data } = await api.post("/ai/room-design", {
        room_type: roomType,
        style,
        color_theme: colorTheme || null,
        notes: notes || null,
        source_image: sourceImage || null,
      });
      setResult(data);
      trackEvent("ai_room_generated", {
        event_category: "ai_studio",
        category,
        room_type: roomType,
        style,
        has_panorama: !!data?.panorama,
        with_photo: !!sourceImage,
      });
      toast.success("Your room design is ready!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="ai-studio-page" className="pt-32 pb-28 relative z-10 bg-[#F9F8F6]">
      <Seo
        title="AI Room & Commercial Interior Design Generator — ZEBO Kolkata"
        description="Generate photorealistic 2D + 360° interiors in seconds — homes, offices, restaurants, cafés, retail shops and salons. Free AI interior design tool by ZEBO India."
        path="/ai-studio"
      />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2"><Sparkles size={14} /> ZEBO AI Studio</p>
          <h1 className="font-serif text-6xl sm:text-7xl font-light tracking-tight text-[#143D63] leading-[0.95] max-w-3xl">
            Generate your space, instantly
          </h1>
          <p className="text-[#143D63]/70 mt-6 max-w-xl">Pick a space — home, office, restaurant, café or retail — choose a style, and let our AI render a photorealistic 2D view and a 360° walkthrough.</p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-8 mt-14">
          {/* Controls */}
          <div className="lg:col-span-5">
            <div className="border border-[#143D63]/15 bg-white p-7 space-y-7">
              <div>
                <p className="overline text-[#143D63]/60 mb-3">Space</p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <Chip
                      key={c}
                      active={category === c}
                      data-testid={`category-${c.toLowerCase()}`}
                      onClick={() => switchCategory(c)}
                    >
                      {c}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-[#143D63]/60 mb-3">
                  {category === "Commercial" ? "Commercial space" : "Room type"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {ROOMS_BY_CATEGORY[category].map((r) => (
                    <Chip key={r} active={roomType === r} data-testid={`room-${r.toLowerCase().replace(/[ /]+/g, "-")}`} onClick={() => setRoomType(r)}>{r}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-[#143D63]/60 mb-3">Style</p>
                <div className="flex flex-wrap gap-2">
                  {STYLES.map((s) => (
                    <Chip key={s} active={style === s} data-testid={`style-${s.toLowerCase().replace(/ /g, "-")}`} onClick={() => setStyle(s)}>{s}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-[#143D63]/60 mb-3">Colour mood (optional)</p>
                <input
                  data-testid="ai-color-input"
                  value={colorTheme}
                  onChange={(e) => setColorTheme(e.target.value)}
                  placeholder="e.g. warm beige, walnut & brass"
                  className="w-full border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
                />
              </div>
              <div>
                <p className="overline text-[#143D63]/60 mb-3">Extra details (optional)</p>
                <textarea
                  data-testid="ai-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={category === "Commercial" ? "e.g. reception desk, brand accent wall, dine-in seating for 24" : "e.g. island counter, lots of natural light, plants"}
                  className="w-full border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none resize-none"
                />
              </div>
              <div>
                <p className="overline text-[#143D63]/60 mb-3">Reference photo (optional)</p>
                {sourceImage ? (
                  <div className="relative border border-[#143D63]/20 bg-white p-2">
                    <img src={sourceImage} alt="reference" data-testid="ai-source-preview" className="w-full h-40 object-cover" />
                    <button
                      type="button"
                      onClick={() => setSourceImage(null)}
                      data-testid="ai-source-remove"
                      className="absolute top-3 right-3 bg-[#8B0000] text-[#F9F8F6] p-1.5 hover:bg-[#143D63] transition-colors"
                      aria-label="Remove reference photo"
                    >
                      <X size={14} />
                    </button>
                    <p className="text-[11px] text-[#143D63]/60 mt-2 px-1">AI will redesign this exact space in your chosen style.</p>
                  </div>
                ) : (
                  <>
                    <CameraCapture onCapture={setSourceImage} />
                    <p className="text-[11px] text-[#143D63]/60 mt-2">Snap a real room / wall / floor plan and let the AI make it over — architectural bones preserved.</p>
                  </>
                )}
              </div>
              <button
                data-testid="ai-generate-btn"
                onClick={generate}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Designing…</> : <><Sparkles size={18} /> Generate Design</>}
              </button>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-7">
            {result && result.panorama && (
              <div className="flex items-center gap-1 mb-3 border border-[#143D63]/15 bg-white w-fit">
                <button
                  data-testid="view-2d-btn"
                  onClick={() => setView("2d")}
                  className={`px-5 py-2 text-sm font-bold transition-colors ${view === "2d" ? "bg-[#143D63] text-[#F9F8F6]" : "text-[#143D63]"}`}
                >
                  2D
                </button>
                <button
                  data-testid="view-360-btn"
                  onClick={() => {
                    setView("360");
                    trackEvent("view_360_open", { event_category: "ai_studio", room_type: roomType, style });
                  }}
                  className={`px-5 py-2 text-sm font-bold transition-colors ${view === "360" ? "bg-[#143D63] text-[#F9F8F6]" : "text-[#143D63]"}`}
                >
                  360°
                </button>
              </div>
            )}
            <div className="border border-[#143D63]/15 bg-[#143D63] aspect-[4/3] relative overflow-hidden flex items-center justify-center">
              <AnimatePresence mode="wait">
                {loading && (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center px-8">
                    <Loader2 className="animate-spin text-[#F8C21C] mx-auto mb-5" size={40} />
                    <p className="font-serif text-3xl text-[#F9F8F6] font-light">Rendering your {style.toLowerCase()} {roomType.toLowerCase()}…</p>
                    <p className="text-[#F9F8F6]/60 text-sm mt-2">Creating a flat render + a 360° panorama — this takes ~20–40 seconds.</p>
                  </motion.div>
                )}
                {!loading && result && view === "360" && result.panorama && (
                  <div key="pano" className="absolute inset-0">
                    <PanoramaViewer src={result.panorama} />
                  </div>
                )}
                {!loading && result && view === "2d" && (
                  <motion.img
                    key="result"
                    data-testid="ai-result-image"
                    src={result.image}
                    alt="AI generated room design"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full h-full object-cover"
                  />
                )}
                {!loading && !result && (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center px-8">
                    <Sparkles className="text-[#F8C21C] mx-auto mb-5" size={40} />
                    <p className="font-serif text-3xl text-[#F9F8F6] font-light">Your design will appear here</p>
                    <p className="text-[#F9F8F6]/60 text-sm mt-2">Configure the panel and hit Generate — you'll get a flat view + a 360° walkthrough.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {result && (
              <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <a
                  href={result.image}
                  download="zebo-ai-design.png"
                  data-testid="ai-download-btn"
                  className="inline-flex items-center gap-2 text-[#143D63] font-medium link-underline"
                >
                  <Download size={16} /> Download image
                </a>
                <button
                  onClick={attachAndBook}
                  data-testid="ai-to-consult-btn"
                  className="group inline-flex items-center gap-2 bg-[#8B0000] text-[#F9F8F6] px-6 py-3 text-sm font-bold hover:bg-[#143D63] transition-colors duration-300"
                >
                  Attach &amp; Book Consultation
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
